<template>
  <div dir="rtl" class="mr-8 px-4 sm:px-6 lg:px-8">
    <!-- Alert -->
    <div
      v-if="alert.show"
      :class="[
        'mb-6 p-4 rounded-md border',
        alert.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' : '',
        alert.type === 'danger' ? 'bg-red-50 border-red-200 text-red-800' : '',
        alert.type === 'warning' ? 'bg-yellow-50 border-yellow-200 text-yellow-800' : ''
      ]"
    >
      {{ alert.message }}
    </div>

    <!-- Page Header -->
    <div class="sm:flex sm:items-center">
      <div class="sm:flex-auto">
        <h1 class="text-xl font-semibold text-gray-900">التحديث الإجباري لكل فرع</h1>
        <p class="mt-2 text-sm text-gray-700">
          تحديد أقل نسخة مسموح بها من تطبيق الجوال لكل فرع. المستخدمون الذين لديهم
          نسخة أقدم ستظهر لهم شاشة تحديث إلزامية ولن يتمكنوا من استخدام التطبيق.
        </p>
      </div>
      <div class="mt-4 sm:mt-0 sm:flex-none">
        <button class="enabled-button" @click="openBulkModal">
          تطبيق على كل الفروع
        </button>
      </div>
    </div>

    <!-- How the gate actually behaves in the shipped app -->
    <div class="mt-4 p-4 rounded-md border bg-yellow-50 border-yellow-200 text-yellow-800 text-sm">
      <strong>كيف يعمل الإجبار فعليًا:</strong>
      الشاشة الإلزامية تظهر بناءً على <strong>أقل نسخة</strong> فقط — أي عندما تكون نسخة
      المستخدم أقدم من القيمة المحددة. أما مفتاح «تفعيل التحديث الإجباري» فلا يقرأه
      الإصدار الحالي من التطبيق ووجوده هنا للتوثيق فقط.
      لتعطيل الإجبار: اترك حقل «أقل نسخة» فارغًا.
    </div>

    <!-- Search + filter -->
    <div class="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
      <div class="relative">
        <input
          v-model="searchTerm"
          type="text"
          placeholder="البحث في الفروع..."
          class="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm pr-3"
        />
      </div>
      <div class="relative">
        <select
          v-model="filterStatus"
          class="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
        >
          <option value="all">كل الفروع</option>
          <option value="enforced">يوجد إجبار</option>
          <option value="off">بدون إجبار</option>
        </select>
      </div>
      <div class="text-sm text-gray-600 bg-blue-50 px-3 py-2 rounded-lg flex items-center">
        إجمالي الفروع: {{ rows.length }}
      </div>
    </div>

    <!-- Table -->
    <div class="mt-8 flex flex-col">
      <div v-if="loading" class="text-center py-12">
        <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p class="mt-2 text-gray-600">جاري التحميل...</p>
      </div>

      <div v-else-if="filteredRows.length === 0" class="text-center py-12">
        <p class="text-gray-600">لا توجد فروع مطابقة.</p>
      </div>

      <div v-else class="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div class="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
          <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
            <table class="min-w-full divide-y divide-customHafadhColor">
              <thead class="bg-customHafadhColor text-center">
                <tr>
                  <th class="py-3.5 pr-4 pl-3 text-right text-sm font-semibold text-gray-900 sm:pr-6">#</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">اسم الفرع</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">أقل نسخة Android</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">أقل نسخة iOS</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">مفتاح Android</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">مفتاح iOS</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">إجراءات</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-200 bg-white">
                <tr v-for="(row, index) in filteredRows" :key="row.tenant.id">
                  <td class="whitespace-nowrap py-4 pr-4 pl-3 text-sm text-gray-900 sm:pr-6">{{ index + 1 }}</td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm font-medium text-gray-900">
                    {{ row.tenant.name }}
                    <div class="text-xs text-gray-500">{{ row.tenant.code || row.tenant.id }}</div>
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm font-mono"
                      :class="row.config?.MIN_VERSION_ANDROID ? 'text-gray-900' : 'text-gray-400'">
                    {{ row.config?.MIN_VERSION_ANDROID || 'غير محدد' }}
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm font-mono"
                      :class="row.config?.MIN_VERSION_IOS ? 'text-gray-900' : 'text-gray-400'">
                    {{ row.config?.MIN_VERSION_IOS || 'غير محدد' }}
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm">
                    <span :class="badgeClass(row.config?.FORCE_UPDATE_ANDROID)">
                      {{ parseBool(row.config?.FORCE_UPDATE_ANDROID) ? 'مُفعّل' : 'مُعطّل' }}
                    </span>
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm">
                    <span :class="badgeClass(row.config?.FORCE_UPDATE_IOS)">
                      {{ parseBool(row.config?.FORCE_UPDATE_IOS) ? 'مُفعّل' : 'مُعطّل' }}
                    </span>
                  </td>
                  <td class="whitespace-nowrap py-4 pr-4 pl-3 text-sm font-medium sm:pr-6">
                    <button class="enabled-button" @click="openModal(row)">تعديل</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Single-tenant Edit Modal -->
    <div v-if="showModal" class="popup" dir="rtl">
      <div class="popup-content" style="max-width: 640px; max-height: 90vh; overflow-y: auto;">
        <h3 class="text-lg leading-6 font-medium text-gray-900 mb-4">
          التحديث الإجباري — {{ activeTenant?.name }}
        </h3>

        <form @submit.prevent="handleSave">
          <div class="grid grid-cols-1 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">أقل نسخة Android مسموح بها</label>
              <input
                v-model="formData.MIN_VERSION_ANDROID"
                type="text"
                placeholder="مثال: 1.4.0 — اتركه فارغًا لتعطيل الإجبار"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm font-mono"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">أقل نسخة iOS مسموح بها</label>
              <input
                v-model="formData.MIN_VERSION_IOS"
                type="text"
                placeholder="مثال: 1.4.0 — اتركه فارغًا لتعطيل الإجبار"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm font-mono"
              />
            </div>

            <label class="flex items-center justify-between rounded-md border border-gray-200 p-3">
              <span class="text-sm font-medium text-gray-700">تفعيل التحديث الإجباري (Android)</span>
              <input
                type="checkbox"
                v-model="formData.FORCE_UPDATE_ANDROID"
                class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
            </label>

            <label class="flex items-center justify-between rounded-md border border-gray-200 p-3">
              <span class="text-sm font-medium text-gray-700">تفعيل التحديث الإجباري (iOS)</span>
              <input
                type="checkbox"
                v-model="formData.FORCE_UPDATE_IOS"
                class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
            </label>
          </div>

          <div class="mt-6 flex justify-end gap-3">
            <button
              type="button"
              @click="closeModal"
              class="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:text-sm"
              :disabled="saving"
            >
              إلغاء
            </button>
            <button
              type="submit"
              class="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-customHafadhColor text-base font-medium text-white hover:bg-customHafadhColor/90 sm:text-sm"
              :disabled="saving"
            >
              {{ saving ? 'جاري الحفظ...' : 'حفظ' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Apply-to-all Modal -->
    <div v-if="showBulkModal" class="popup" dir="rtl">
      <div class="popup-content" style="max-width: 640px; max-height: 90vh; overflow-y: auto;">
        <h3 class="text-lg leading-6 font-medium text-gray-900 mb-2">تطبيق على كل الفروع</h3>

        <div class="mb-4 p-3 rounded-md border bg-red-50 border-red-200 text-red-800 text-sm">
          هذا الإجراء يُطبّق القيم أدناه على
          <strong>{{ bulkTargetCount }}</strong>
          فرعًا دفعة واحدة. تحديد أقل نسخة أعلى من النسخة المنشورة يمنع جميع
          المستخدمين في كل الفروع من استخدام التطبيق.
          <br />
          الحقول المتروكة فارغة لن تُرسل ولن تُغيّر القيم الحالية لأي فرع.
        </div>

        <form @submit.prevent="handleApplyToAll">
          <div class="grid grid-cols-1 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">أقل نسخة Android مسموح بها</label>
              <input
                v-model="bulkForm.MIN_VERSION_ANDROID"
                type="text"
                placeholder="اتركه فارغًا لعدم التغيير"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm font-mono"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">أقل نسخة iOS مسموح بها</label>
              <input
                v-model="bulkForm.MIN_VERSION_IOS"
                type="text"
                placeholder="اتركه فارغًا لعدم التغيير"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm font-mono"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">مفتاح التحديث الإجباري (Android)</label>
              <select
                v-model="bulkForm.FORCE_UPDATE_ANDROID"
                class="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm sm:text-sm"
              >
                <option value="">بدون تغيير</option>
                <option value="true">تفعيل</option>
                <option value="false">تعطيل</option>
              </select>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">مفتاح التحديث الإجباري (iOS)</label>
              <select
                v-model="bulkForm.FORCE_UPDATE_IOS"
                class="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm sm:text-sm"
              >
                <option value="">بدون تغيير</option>
                <option value="true">تفعيل</option>
                <option value="false">تعطيل</option>
              </select>
            </div>

            <label class="flex items-center justify-between rounded-md border border-gray-200 p-3">
              <span class="text-sm font-medium text-gray-700">الفروع النشطة فقط</span>
              <input
                type="checkbox"
                v-model="bulkForm.onlyActive"
                class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
            </label>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                للتأكيد، اكتب «{{ CONFIRM_WORD }}» في الحقل التالي
              </label>
              <input
                v-model="bulkConfirm"
                type="text"
                :placeholder="CONFIRM_WORD"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-red-500 focus:border-red-500 sm:text-sm"
              />
            </div>
          </div>

          <div class="mt-6 flex justify-end gap-3">
            <button
              type="button"
              @click="closeBulkModal"
              class="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:text-sm"
              :disabled="saving"
            >
              إلغاء
            </button>
            <button
              type="submit"
              class="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 sm:text-sm disabled:opacity-50"
              :disabled="saving || bulkConfirm.trim() !== CONFIRM_WORD"
            >
              {{ saving ? 'جاري التطبيق...' : 'تطبيق على الكل' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import {
  listAppUpdateConfigs,
  upsertTenantAppUpdateConfig,
  applyAppUpdateConfigToAll,
} from '@/services/appUpdateConfigs.js';

// Typed into the bulk modal before the apply button enables.
const CONFIRM_WORD = 'تطبيق';

// Stored as the literal strings 'true'/'false' by the API — see the controller.
const parseBool = (value) => String(value ?? '').trim().toLowerCase() === 'true';

const emptyForm = () => ({
  MIN_VERSION_ANDROID: '',
  MIN_VERSION_IOS: '',
  FORCE_UPDATE_ANDROID: false,
  FORCE_UPDATE_IOS: false,
});

const emptyBulkForm = () => ({
  MIN_VERSION_ANDROID: '',
  MIN_VERSION_IOS: '',
  FORCE_UPDATE_ANDROID: '',
  FORCE_UPDATE_IOS: '',
  onlyActive: true,
});

export default defineComponent({
  name: 'TenantAppUpdateConfigsManagement',
  data() {
    return {
      CONFIRM_WORD,
      rows: [],
      loading: false,
      saving: false,
      searchTerm: '',
      filterStatus: 'all',
      showModal: false,
      showBulkModal: false,
      activeTenant: null,
      formData: emptyForm(),
      bulkForm: emptyBulkForm(),
      bulkConfirm: '',
      alert: { show: false, type: 'success', message: '' },
    };
  },
  computed: {
    filteredRows() {
      let rows = this.rows;

      if (this.searchTerm) {
        const term = this.searchTerm.toLowerCase();
        rows = rows.filter((row) =>
          row.tenant.name?.toLowerCase().includes(term) ||
          row.tenant.code?.toLowerCase().includes(term),
        );
      }

      if (this.filterStatus !== 'all') {
        // "Enforced" means a minimum version is set — that, not the toggle, is
        // what actually gates users in the shipped app.
        rows = rows.filter((row) => {
          const enforced = Boolean(row.config?.MIN_VERSION_ANDROID || row.config?.MIN_VERSION_IOS);
          return this.filterStatus === 'enforced' ? enforced : !enforced;
        });
      }

      return rows;
    },
    bulkTargetCount() {
      if (!this.bulkForm.onlyActive) return this.rows.length;
      return this.rows.filter((row) => row.tenant.is_active).length;
    },
  },
  mounted() {
    this.loadAll();
  },
  methods: {
    parseBool,
    async loadAll() {
      this.loading = true;
      try {
        this.rows = await listAppUpdateConfigs();
      } catch (err) {
        console.error('Failed to load app update configs', err);
        this.showAlert('danger', err.response?.data?.result?.mesAr || 'فشل في تحميل البيانات');
      } finally {
        this.loading = false;
      }
    },
    badgeClass(value) {
      return parseBool(value)
        ? 'px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800'
        : 'px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600';
    },
    openModal(row) {
      this.activeTenant = row.tenant;
      // Unlike the Agora page these aren't secrets, so prefill the current state —
      // the operator needs to see what a branch is on before changing it.
      this.formData = {
        MIN_VERSION_ANDROID: row.config?.MIN_VERSION_ANDROID || '',
        MIN_VERSION_IOS: row.config?.MIN_VERSION_IOS || '',
        FORCE_UPDATE_ANDROID: parseBool(row.config?.FORCE_UPDATE_ANDROID),
        FORCE_UPDATE_IOS: parseBool(row.config?.FORCE_UPDATE_IOS),
      };
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.activeTenant = null;
      this.formData = emptyForm();
    },
    async handleSave() {
      if (!this.activeTenant) return;
      this.saving = true;

      // The form is prefilled, so every field is sent. An empty version string
      // clears that row server-side, which is how the operator turns the gate off.
      const payload = {
        MIN_VERSION_ANDROID: String(this.formData.MIN_VERSION_ANDROID || '').trim(),
        MIN_VERSION_IOS: String(this.formData.MIN_VERSION_IOS || '').trim(),
        FORCE_UPDATE_ANDROID: Boolean(this.formData.FORCE_UPDATE_ANDROID),
        FORCE_UPDATE_IOS: Boolean(this.formData.FORCE_UPDATE_IOS),
      };

      try {
        await upsertTenantAppUpdateConfig(this.activeTenant.id, payload);
        await this.loadAll();
        this.showAlert('success', 'تم حفظ إعدادات التحديث الإجباري');
        this.closeModal();
      } catch (err) {
        console.error('Failed to save app update config', err);
        this.showAlert('danger', this.errorMessage(err, 'فشل في حفظ الإعدادات'));
      } finally {
        this.saving = false;
      }
    },
    openBulkModal() {
      this.bulkForm = emptyBulkForm();
      this.bulkConfirm = '';
      this.showBulkModal = true;
    },
    closeBulkModal() {
      this.showBulkModal = false;
      this.bulkForm = emptyBulkForm();
      this.bulkConfirm = '';
    },
    async handleApplyToAll() {
      if (this.bulkConfirm.trim() !== CONFIRM_WORD) return;

      // Only send what the operator actually filled in. Anything omitted is left
      // untouched per tenant — otherwise a blank field would wipe every branch.
      const payload = { onlyActive: Boolean(this.bulkForm.onlyActive) };

      ['MIN_VERSION_ANDROID', 'MIN_VERSION_IOS'].forEach((code) => {
        const value = String(this.bulkForm[code] || '').trim();
        if (value !== '') payload[code] = value;
      });

      ['FORCE_UPDATE_ANDROID', 'FORCE_UPDATE_IOS'].forEach((code) => {
        const value = this.bulkForm[code];
        if (value === 'true' || value === 'false') payload[code] = value === 'true';
      });

      if (Object.keys(payload).length <= 1) {
        this.showAlert('warning', 'لم تُحدَّد أي قيمة للتطبيق');
        return;
      }

      this.saving = true;
      try {
        const fresh = await applyAppUpdateConfigToAll(payload);
        this.rows = Array.isArray(fresh) && fresh.length ? fresh : this.rows;
        if (!Array.isArray(fresh) || !fresh.length) await this.loadAll();
        this.showAlert('success', 'تم تطبيق الإعدادات على الفروع');
        this.closeBulkModal();
      } catch (err) {
        console.error('Failed to apply app update config to all tenants', err);
        this.showAlert('danger', this.errorMessage(err, 'فشل في تطبيق الإعدادات'));
      } finally {
        this.saving = false;
      }
    },
    errorMessage(err, fallback) {
      const result = err.response?.data?.result;
      // Validation errors come back as { data: { FIELD: [msg] } } — surface the first.
      const firstFieldError = result?.data && typeof result.data === 'object'
        ? Object.values(result.data).flat()[0]
        : null;
      return firstFieldError || result?.mesAr || err.response?.data?.mesAr || fallback;
    },
    showAlert(type, message) {
      this.alert = { show: true, type, message };
      setTimeout(() => {
        this.alert.show = false;
      }, 4000);
    },
  },
});
</script>
